'use client';
/* eslint-disable @nrwl/nx/enforce-module-boundaries */
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    Input,
    Button,
    DialogDescription,
    useToast,
} from '@quadminds/mindkit';
import useCreateFolder from '@quadminds/mindy-app/hooks/useCreateFolder';
import { IAuthMeV2OutputDto } from '@quadminds/shared/auth-system';
import { useQueryClient } from '@tanstack/react-query';
import type React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

interface RenameDialogProps {
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
    onClose: () => void;
}

export function CreateFolder({ isOpen, onClose, onOpenChange }: RenameDialogProps) {
    const { t } = useTranslation();
    const [newFolderName, setNewName] = useState('');
    const [error, setError] = useState('');
    const { mutateAsync: triggerCreateFolder, isPending } = useCreateFolder();
    const location = useLocation();
    const navigate = useNavigate();
    const { toast } = useToast();
    const ids = location.pathname
        .split('/') // Dividir por cada nivel
        .filter(Boolean);
    const queryClient = useQueryClient();
    const user: IAuthMeV2OutputDto | undefined = queryClient.getQueryData(['userInfo']); // Retrieve userInfo from the cache
    const baseKnowledgeUrl = user?.currentOrganization;

    if (baseKnowledgeUrl === undefined) {
        navigate('/user-not-found');
    }

    const handleSubmit = () => {
        if (!newFolderName.trim()) {
            setError('El nombre no puede estar vacío');
            return;
        }

        // Check for invalid characters in file/folder names
        if (/[\\/:*?"<>|]/.test(newFolderName)) {
            setError('El nombre no puede contener los siguientes caracteres: \\ / : * ? " < > |');
            return;
        }

        triggerCreateFolder({
            name: newFolderName, // folder name
            parentId: ids[1] === baseKnowledgeUrl ? undefined : ids[1], // parent folder ID, if any
        })
            .then(() => {
                toast({
                    title: t('toast.createFolder.success.title'),
                    description: t('toast.createFolder.success.description', { name: newFolderName }),
                    variant: 'default',
                });
                queryClient.invalidateQueries({ queryKey: ['documents'] });
                setNewName('');
                onClose();
            })
            .catch((error) => {
                toast({
                    title: t('modals.createFolder.error.title'),
                    description: error.message || t('modals.createFolder.error.description'),
                    variant: 'destructive',
                });
            });
    };

    return (
        <Dialog open={isOpen} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                    <DialogTitle>{t('modals.createFolder.title')}</DialogTitle>
                    <DialogDescription>{t('modals.createFolder.description')}</DialogDescription>
                </DialogHeader>
                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        handleSubmit();
                    }}
                >
                    <div className="grid gap-4 py-4">
                        <div className="grid gap-2">
                            <Input
                                id="folderName"
                                value={newFolderName}
                                onChange={(e) => setNewName(e.target.value)}
                                placeholder={t('modals.createFolder.inputPlaceholder')}
                                autoFocus
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => onClose()} disabled={isPending}>
                            {t('modals.common.cancel')}
                        </Button>
                        <Button type="submit" disabled={isPending}>
                            {t('modals.createFolder.create')}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
